import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';

interface CodeInputProps {
  value: string;
  onChange: (value: string) => void;
  onVerify: () => void;
  loading: boolean;
}

// ✅ STYLED COMPONENTS
const Container = styled.div`
  margin: 24px 0;
`;

const InputGroup = styled.div`
  display: flex;
  gap: 12px;
  justify-content: center;
  margin-bottom: 24px;
`;

const CodeInputField = styled.input<{ hasError: boolean }>`
  width: 50px;
  height: 60px;
  border: 2px solid ${props => props.hasError ? '#e74c3c' : '#e1e5e9'};
  border-radius: 8px;
  font-size: 24px;
  font-weight: 600;
  text-align: center;
  color: #2c3e50;
  transition: all 0.2s;
  
  &:focus {
    outline: none;
    border-color: #3498db;
    box-shadow: 0 0 0 3px rgba(52, 152, 219, 0.1);
  }
  
  &:disabled {
    background-color: #f8f9fa;
    cursor: not-allowed;
  }
`;

const ErrorMessage = styled.div`
  color: #e74c3c;
  font-size: 14px;
  text-align: center;
  margin-bottom: 16px;
`;

const VerifyButton = styled.button<{ disabled: boolean }>`
  background: ${props => props.disabled ? '#bdc3c7' : '#3498db'};
  color: white;
  border: none;
  border-radius: 8px;
  padding: 14px 32px;
  font-size: 16px;
  font-weight: 500;
  cursor: ${props => props.disabled ? 'not-allowed' : 'pointer'};
  transition: all 0.2s;
  width: 100%;
  
  &:hover:not(:disabled) {
    background: #2980b9;
    transform: translateY(-1px);
  }
  
  &:disabled {
    opacity: 0.7;
  }
`;

const LoadingSpinner = styled.div`
  display: inline-block;
  width: 16px;
  height: 16px;
  border: 2px solid #ffffff;
  border-radius: 50%;
  border-top-color: transparent;
  animation: spin 1s ease-in-out infinite;
  margin-right: 8px;
  
  @keyframes spin {
    to { transform: rotate(360deg); }
  }
`;

export const CodeInput: React.FC<CodeInputProps> = ({
  value,
  onChange,
  onVerify,
  loading
}) => {
  const [error, setError] = useState('');
  const inputRefs = useRef<(HTMLInputElement | null)[]>([]);

  useEffect(() => {
    // Preencher inputs com o valor atual
    const codeArray = value.split('');
    inputRefs.current.forEach((input, index) => {
      if (input) {
        input.value = codeArray[index] || '';
      }
    });
  }, [value]);

  const handleInputChange = (index: number, inputValue: string) => {
    if (inputValue.length > 1) {
      inputValue = inputValue.slice(-1);
    }

    const newCode = value.split('');
    newCode[index] = inputValue;
    const newCodeString = newCode.join('');
    
    onChange(newCodeString);

    // Mover para próximo input
    if (inputValue && index < 5) {
      inputRefs.current[index + 1]?.focus();
    }

    // Limpar erro
    if (error) {
      setError('');
    }
  };

  const handleKeyDown = (index: number, e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Backspace' && !inputRefs.current[index]?.value && index > 0) {
      inputRefs.current[index - 1]?.focus();
    }
  };

  const handleVerify = () => {
    if (value.length !== 6) {
      setError('Digite o código completo de 6 dígitos');
      return;
    }
    
    if (!/^\d{6}$/.test(value)) {
      setError('O código deve conter apenas números');
      return;
    }

    onVerify();
  };

  return (
    <Container>
      <InputGroup>
        {Array.from({ length: 6 }).map((_, index) => (
          <CodeInputField
            key={index}
            ref={(el) => (inputRefs.current[index] = el)}
            type="text"
            maxLength={1}
            inputMode="numeric"
            pattern="[0-9]*"
            onChange={(e) => handleInputChange(index, e.target.value)}
            onKeyDown={(e) => handleKeyDown(index, e)}
            disabled={loading}
            hasError={!!error}
          />
        ))}
      </InputGroup>

      {error && <ErrorMessage>{error}</ErrorMessage>}

      <VerifyButton 
        onClick={handleVerify} 
        disabled={loading || value.length !== 6}
      >
        {loading ? (
          <>
            <LoadingSpinner />
            Verificando...
          </>
        ) : (
          'Verificar e Ativar 2FA'
        )}
      </VerifyButton>
    </Container>
  );
};